/* ═══════════════════════════════════════════════════════════════
   The Designer — spec-domain commands and document invariants.

   ProseMirror already owns the caret, selection, undo history, IME and
   the clipboard; nothing here re-implements any of that. These are the
   SPEC rules on top:

     · Tab / Shift+Tab promote and demote a paragraph's level — EXCEPT
       inside a table, where Word's rule wins and they walk cells (the
       framework's own goToNextCell / goToPreviousCell)
     · every block carries exactly one stable, unique id
     · Ctrl+S flushes the autosave instead of printing the page
     · Ctrl+D duplicates the selected blocks; Ctrl+F opens find
     · whole-block operations: duplicate, delete, insert image /
       table / related sections — the menu and toolbar call these
   ═══════════════════════════════════════════════════════════════ */

import { Extension } from '@tiptap/core';
import { NodeSelection, Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { isInTable } from '@tiptap/pm/tables';
import type { Selection, Transaction } from '@tiptap/pm/state';
import type { EditorState } from '@tiptap/pm/state';
import type { Node as PMNode, Schema } from '@tiptap/pm/model';
import { newBlockId } from './schema';
import { buildTableNode, TABLE_NODE_NAME } from './tables';
import { openFind } from './search';
import { RELATED_SECTIONS_KIND } from '../../shared/related-sections';

export const uniqueIdsPluginKey = new PluginKey('designerUniqueIds');

/** Names of every node type that represents one ContentBlock. */
export const BLOCK_NODE_NAMES = ['specParagraph', TABLE_NODE_NAME, 'specImage', 'smartBlock'];

/**
 * An EXPLICIT set of blocks, named by id, that outranks the text selection
 * for whole-block operations.
 *
 * The Spec Studio's block layer (`spec-studio/blockSelect.ts`) installs a
 * plugin under this key so a gutter multi-selection — which can be
 * non-contiguous, and which deliberately leaves the caret where it was — is
 * what "the blocks I mean" resolves to. The key lives HERE, next to
 * `selectedBlocks`, because this file owns that definition; the Designer
 * never installs the plugin, so `getState` is undefined there and every
 * command behaves exactly as it always has.
 */
export const explicitBlocksKey = new PluginKey<{ ids: string[] }>('designerExplicitBlocks');

/**
 * Every block owns exactly one id, forever.
 *
 * Splitting a paragraph, pasting, and duplicating all produce nodes that
 * share their source's `blockId`. Rather than patch each of those paths, this
 * plugin restores the invariant after ANY transaction — so a path we haven't
 * thought of yet cannot corrupt the document either. Ids are only ever minted,
 * never reassigned on a block that already holds a unique one, which keeps
 * issuance diffs stable across edits.
 */
export const UniqueBlockIds = Extension.create({
  name: 'designerUniqueIds',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: uniqueIdsPluginKey,
        appendTransaction: (transactions, _oldState, newState) => {
          if (!transactions.some((tr) => tr.docChanged)) return null;
          const seen = new Set<string>();
          let tr: Transaction | null = null;
          newState.doc.forEach((node, offset) => {
            if (!BLOCK_NODE_NAMES.includes(node.type.name)) return;
            const id = node.attrs.blockId as string;
            if (id && !seen.has(id)) {
              seen.add(id);
              return;
            }
            const fresh = newBlockId();
            seen.add(fresh);
            tr = (tr || newState.tr).setNodeAttribute(offset, 'blockId', fresh);
          });
          // Housekeeping must never enter the undo stack. If it did, every
          // Ctrl+Z would first undo an id fix and only then the user's edit,
          // and redo would replay the two out of step — the document comes
          // back rearranged. (Caught by the R1 harness's undo/redo burst.)
          if (tr) (tr as Transaction).setMeta('addToHistory', false);
          return tr;
        },
      }),
    ];
  },
});

export interface SpecKeymapOptions {
  /** Ctrl/Cmd+S — flush pending autosave. */
  onSave?: () => void;
  /** Escape — the keyboard user's way out of the document (see below). */
  onEscape?: () => void;
}

/** Clamp to the nine MasterFormat levels. */
const clampLevel = (n: number) => Math.max(1, Math.min(9, n));

/**
 * Change the level of every block touched by the selection. Word's Tab
 * behaviour, applied to a spec: the paragraph moves between MasterFormat
 * levels, its number and indent follow, and the text is never touched.
 */
function shiftLevel(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
  delta: number
) {
  let tr: Transaction | null = null;
  let changed = false;
  // `selectedBlocks` — not a second doc walk — so a gutter selection promotes
  // exactly the blocks it names (and the caret case is unchanged).
  for (const { node, pos } of selectedBlocks(state)) {
    const next = clampLevel(((node.attrs.level as number) || 1) + delta);
    if (next === node.attrs.level) continue;
    changed = true;
    tr = (tr || state.tr).setNodeAttribute(pos, 'level', next);
  }
  if (!changed || !tr) return false;
  if (dispatch) dispatch(tr);
  return true;
}

/**
 * The spec keymap. Everything NOT listed here is deliberately left to
 * ProseMirror: Enter splits, Backspace merges, Ctrl+Z/Y undo, arrows and
 * selection all behave like a document editor because the framework — not
 * this app — implements them.
 */
export const SpecKeymap = Extension.create<SpecKeymapOptions>({
  name: 'designerKeymap',

  addOptions() {
    return { onSave: undefined, onEscape: undefined };
  },

  addKeyboardShortcuts() {
    return {
      // Inside a table Tab is Word's Tab: the next cell, not a level change.
      // Returning false hands the key to the NEXT keymap in the stack, which
      // is the framework's own table keymap (goToNextCell / goToPreviousCell,
      // and a new row when Tab leaves the last cell). Changing a table's own
      // level still works — the block menu's "Change level" — and a table
      // selected as a whole block (no caret in a cell) still tabs levels.
      Tab: ({ editor }) =>
        isInTable(editor.state)
          ? false
          : shiftLevel(editor.state, editor.view.dispatch.bind(editor.view), +1),
      'Shift-Tab': ({ editor }) =>
        isInTable(editor.state)
          ? false
          : shiftLevel(editor.state, editor.view.dispatch.bind(editor.view), -1),
      'Mod-s': () => {
        this.options.onSave?.();
        return true;
      },
      // Find — the universal shortcut (DESIGN §1). A collapsed selection
      // opens the bar with the last query; a one-line selection pre-fills it.
      'Mod-f': ({ editor }) => {
        const { state } = editor;
        const { from, to, empty } = state.selection;
        const picked = empty ? '' : state.doc.textBetween(from, to, '\n');
        const seed = picked && !picked.includes('\n') ? picked.slice(0, 500) : undefined;
        return openFind(state, editor.view.dispatch.bind(editor.view), seed);
      },
      // Duplicate the selected blocks. Swallowed even when nothing is
      // selected so the browser's bookmark dialog can never appear.
      'Mod-d': ({ editor }) => {
        if (editor.isEditable) {
          duplicateSelectedBlocks(editor.state, editor.view.dispatch.bind(editor.view));
        }
        return true;
      },
      // Tab belongs to the spec levels, so it cannot also be "move to the
      // next control" — which would leave a keyboard user trapped in the
      // document (WCAG 2.1.2). Escape is the documented way out: it blurs
      // the editor, and the next Tab continues through the page normally.
      Escape: ({ editor }) => {
        (editor.view.dom as HTMLElement).blur();
        this.options.onEscape?.();
        return true;
      },
      // Inline emphasis — the only four character styles that exist. The
      // forma owns everything else, so there is no font/size/colour path.
      // Highlight is the marker pen (Word's Ctrl+Alt+H); Mod+Shift+H is the
      // second binding because Alt chords are unreliable across platforms.
      'Mod-b': ({ editor }) => editor.commands.toggleMark('bold'),
      'Mod-i': ({ editor }) => editor.commands.toggleMark('italic'),
      'Mod-u': ({ editor }) => editor.commands.toggleMark('underline'),
      'Mod-Alt-h': ({ editor }) => editor.commands.toggleMark('highlight'),
      'Mod-Shift-h': ({ editor }) => editor.commands.toggleMark('highlight'),
    };
  },
});

/** Promote/demote from a button or menu, not just the keyboard. */
export function changeLevel(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
  delta: number
): boolean {
  return shiftLevel(state, dispatch, delta);
}

/** Set an absolute level on the blocks the selection touches. */
export function setLevel(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
  level: number
): boolean {
  const target = clampLevel(level);
  let tr: Transaction | null = null;
  for (const { node, pos } of selectedBlocks(state)) {
    if (node.attrs.level === target) continue;
    tr = (tr || state.tr).setNodeAttribute(pos, 'level', target);
  }
  if (!tr) return false;
  if (dispatch) dispatch(tr);
  return true;
}

/**
 * Whether shifting every level in `levels` by `delta` would keep ALL of them
 * inside the nine MasterFormat levels. The Blocks panel's step buttons
 * (Modules R2 Phase 6, Ruling 9 amendment) move every selected block
 * together or not at all — a level-2 parent with level-3 children shifting
 * to 3/4 is fine, but one block already at level 9 blocks the whole group
 * from going deeper (no structure squashing). Pure and editor-free so the
 * clamp rule is provable without a document.
 */
export function canShiftLevels(levels: number[], delta: number): boolean {
  return levels.length > 0 && levels.every((l) => l + delta >= 1 && l + delta <= 9);
}

/**
 * Shift every block the selection touches by `delta` together, preserving
 * their levels relative to each other — the same uniform delta applied to
 * every block IS "preserve relative levels". Does nothing (returns false, no
 * transaction) if `canShiftLevels` says any block would pass the 1–9
 * boundary. One transaction either way, so one Ctrl+Z reverts the whole
 * group. Unlike Tab/Shift+Tab's `shiftLevel`, which clamps each block
 * independently and is left untouched (R2 Ruling 9: Tab stays as-is).
 */
export function shiftSelectedLevels(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
  delta: number
): boolean {
  const blocks = selectedBlocks(state);
  const levels = blocks.map(({ node }) => (node.attrs.level as number) || 1);
  if (!canShiftLevels(levels, delta)) return false;
  let tr: Transaction | null = null;
  for (const { node, pos } of blocks) {
    const next = ((node.attrs.level as number) || 1) + delta;
    if (next === node.attrs.level) continue;
    tr = (tr || state.tr).setNodeAttribute(pos, 'level', next);
  }
  if (!tr) return false;
  if (dispatch) dispatch(tr);
  return true;
}

/** The group a block belongs to, or '' — the one place the attribute is read,
 *  so "grouped" means the same thing to the gutter, the commands and the
 *  guard. */
export function groupIdOf(node: PMNode): string {
  return ((node.attrs.groupId as string) || '').trim();
}

/**
 * THE group rule, in one function: naming ANY block of a group names the
 * whole group.
 *
 * Applied in exactly two places — `clickBlockHandle` (so a grip click, a
 * Ctrl-click and a Shift-range all take the run as a unit) and `selectedBlocks`
 * (so move, duplicate, delete, level, skip-at-export, continuation, the
 * clipboard triad and hide-in-variant follow, with no second implementation
 * anywhere). Ungrouped ids pass through untouched, which is why every one of
 * those commands behaves exactly as it always has on an ungrouped document.
 */
export function expandToGroups(doc: PMNode, ids: string[]): Set<string> {
  const want = new Set(ids);
  if (!want.size) return want;
  const groups = new Set<string>();
  doc.forEach((node) => {
    if (!BLOCK_NODE_NAMES.includes(node.type.name)) return;
    if (!want.has((node.attrs.blockId as string) || '')) return;
    const gid = groupIdOf(node);
    if (gid) groups.add(gid);
  });
  if (!groups.size) return want;
  doc.forEach((node) => {
    if (!BLOCK_NODE_NAMES.includes(node.type.name)) return;
    const gid = groupIdOf(node);
    if (gid && groups.has(gid)) want.add((node.attrs.blockId as string) || '');
  });
  want.delete('');
  return want;
}

/** The top-level blocks the selection touches (a collapsed caret counts the
 *  block it sits in) — the shared footing for every whole-block operation.
 *  Exported so the Spec Masters Studio's own spec commands stand on the SAME
 *  definition of "the blocks I mean" as the Designer's (Masters Ladder R2).
 *
 *  An explicit gutter selection (`explicitBlocksKey`) OUTRANKS the text
 *  selection when one is present — see that key's note. */
export function selectedBlocks(state: EditorState): Array<{ node: PMNode; pos: number }> {
  const explicit = explicitBlocksKey.getState(state)?.ids;
  if (explicit && explicit.length) {
    const want = expandToGroups(state.doc, explicit);
    const picked: Array<{ node: PMNode; pos: number }> = [];
    state.doc.forEach((node, offset) => {
      if (!BLOCK_NODE_NAMES.includes(node.type.name)) return;
      if (want.has(node.attrs.blockId as string)) picked.push({ node, pos: offset });
    });
    // Fall through to the text selection only if every named block has since
    // left the document — never silently act on the wrong thing.
    if (picked.length) return picked;
  }
  const { from, to } = state.selection;
  const out: Array<{ node: PMNode; pos: number }> = [];
  state.doc.forEach((node, offset) => {
    if (!BLOCK_NODE_NAMES.includes(node.type.name)) return;
    const end = offset + node.nodeSize;
    if (end <= from || offset >= to) {
      if (!(from === to && from >= offset && from <= end)) return;
    }
    out.push({ node, pos: offset });
  });
  return out;
}

/**
 * Duplicate every block the selection touches, inserting the copies right
 * after the last one — Word's Ctrl+D-on-a-row feel. Copies mint fresh ids
 * immediately so issuance diffs never see two blocks claiming one identity.
 */
export function duplicateSelectedBlocks(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined
): boolean {
  const sel = selectedBlocks(state);
  if (!sel.length) return false;
  const last = sel[sel.length - 1];
  const copies = sel.map(({ node }) =>
    node.type.create({ ...node.attrs, blockId: newBlockId() }, node.content, node.marks)
  );
  if (dispatch) {
    dispatch(state.tr.insert(last.pos + last.node.nodeSize, copies).scrollIntoView());
  }
  return true;
}

/**
 * Delete every block the selection touches — whole blocks, one transaction,
 * recoverable with one Ctrl+Z. Deleting the final block leaves one empty
 * paragraph, because a spec document always has somewhere to put the caret.
 *
 * The blocks are removed one range at a time, LAST first, so earlier
 * positions stay valid: a gutter selection can be non-contiguous, and a
 * single first→last span would take the untouched blocks in between with it.
 */
export function deleteSelectedBlocks(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined
): boolean {
  const sel = selectedBlocks(state);
  if (!sel.length) return false;
  const first = sel[0];
  const last = sel[sel.length - 1];
  const end = last.pos + last.node.nodeSize;
  const tr = state.tr;
  if (first.pos === 0 && end === state.doc.content.size && sel.length === countBlocks(state)) {
    tr.replaceWith(0, state.doc.content.size, emptyParagraph(state.schema));
  } else {
    for (let i = sel.length - 1; i >= 0; i--) {
      tr.delete(sel[i].pos, sel[i].pos + sel[i].node.nodeSize);
    }
  }
  // Put the caret at the START of the block that followed the deleted run —
  // or of the block before it when the run ran to the end of the document.
  // Without this the caret stays at a position the deleted blocks used to
  // own, and the user loses their place in the document (Tranche 4 item 1).
  tr.setSelection(caretAtBlockStart(tr.doc, tr.mapping.map(first.pos, -1)));
  if (dispatch) dispatch(tr.scrollIntoView());
  return true;
}

/** The document position of every top-level block, in order. */
function blockOffsets(doc: PMNode): number[] {
  const out: number[] = [];
  doc.forEach((node, offset) => {
    if (BLOCK_NODE_NAMES.includes(node.type.name)) out.push(offset);
  });
  return out;
}

/** A caret at the start of the first block at or after `pos` — the last block
 *  in the document when nothing follows. `near` snaps to the closest position
 *  that can actually hold a caret, so landing on a figure or a table is safe. */
function caretAtBlockStart(doc: PMNode, pos: number): Selection {
  const offsets = blockOffsets(doc);
  const at = offsets.find((o) => o >= pos) ?? offsets[offsets.length - 1] ?? 0;
  return TextSelection.near(doc.resolve(Math.min(at + 1, doc.content.size)), 1);
}

/** How many top-level blocks the document holds. */
function countBlocks(state: EditorState): number {
  let n = 0;
  state.doc.forEach((node) => {
    if (BLOCK_NODE_NAMES.includes(node.type.name)) n++;
  });
  return n;
}

function emptyParagraph(schema: Schema): PMNode {
  return schema.nodes.specParagraph.create({
    blockId: newBlockId(),
    level: 1,
    continuation: false,
    excluded: false,
    meta: {},
  });
}

/** Insert a block after the selection's last block, at that block's level.
 *  Atoms (image / smart token) come back selected so the user sees exactly
 *  what landed and where; the table (real editable content, not an atom)
 *  instead puts the caret in its first cell — ready to type. */
function insertBlockAfterSelection(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
  build: (schema: Schema, level: number) => PMNode
): boolean {
  const sel = selectedBlocks(state);
  const last = sel.length ? sel[sel.length - 1] : null;
  const insertAt = last ? last.pos + last.node.nodeSize : state.doc.content.size;
  const level = last ? (last.node.attrs.level as number) || 1 : 1;
  const node = build(state.schema, level);
  let tr = state.tr.insert(insertAt, node);
  if (node.type.isAtom) {
    tr = tr.setSelection(NodeSelection.create(tr.doc, insertAt));
  } else if (node.type.name === TABLE_NODE_NAME) {
    tr = tr.setSelection(TextSelection.near(tr.doc.resolve(insertAt + 1), 1));
  }
  if (dispatch) dispatch(tr.scrollIntoView());
  return true;
}

/** A figure block from a picked file, at the width it will print. */
export function insertImageBlock(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
  dataUrl: string,
  name: string
): boolean {
  return insertBlockAfterSelection(state, dispatch, (schema, level) =>
    schema.nodes.specImage.create({
      blockId: newBlockId(),
      level,
      continuation: false,
      excluded: false,
      meta: {},
      text: name || '',
      imageData: dataUrl,
      imageWidth: 100,
    })
  );
}

/** A fresh table with a header row (tables.ts's `buildTableNode` — built
 *  entirely from the framework's own `createTable` utility), inserted after
 *  the current block like every other insert here. Default 3×3 — every
 *  existing caller keeps calling this with no third argument; the Tables
 *  toolbox's size picker (T1.2) is the only caller that passes `opts`. */
export function insertTableBlock(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
  opts?: { rows?: number; cols?: number }
): boolean {
  return insertBlockAfterSelection(state, dispatch, (schema, level) =>
    buildTableNode(schema, level, opts)
  );
}

// Smart blocks are cut (MASTERS_LADDER_PLAN §0) with ONE exception (Josh
// 2026-10-02, MODULES_VISION §5): Related sections. It is the only smart kind
// anything can insert. The schema still keeps the smartBlock node and its
// node view so documents carrying a legacy kind still render and export.

/** A Related sections block (shared/related-sections.ts), after the current
 *  block. In a master it is a placeholder; in a project spec the caller
 *  passes params already seeded with the project's sections (hook 3b). */
export function insertRelatedSectionsBlock(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
  smartParams: Record<string, unknown>
): boolean {
  return insertBlockAfterSelection(state, dispatch, (schema, level) =>
    schema.nodes.smartBlock.create({
      blockId: newBlockId(),
      level,
      continuation: false,
      excluded: false,
      meta: { smartBlockKind: RELATED_SECTIONS_KIND, smartParams },
      text: '',
    })
  );
}

/** Rewrite one smart block's `smartParams` — an ordinary document change, so
 *  it dirties the doc, saves, and undoes like any other edit. `history: false`
 *  is for bookkeeping the user did not do (seeding an old block, marking the
 *  picker's rows as seen): it still saves, but Ctrl+Z never "undoes" it. */
export function setBlockSmartParams(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
  blockId: string,
  smartParams: Record<string, unknown>,
  opts: { history?: boolean } = {}
): boolean {
  let target: { node: PMNode; pos: number } | null = null;
  state.doc.forEach((node, pos) => {
    if (!target && node.type.name === 'smartBlock' && node.attrs.blockId === blockId) {
      target = { node, pos };
    }
  });
  if (!target) return false;
  const { node, pos } = target as { node: PMNode; pos: number };
  const meta = (node.attrs.meta as Record<string, unknown> | undefined) || {};
  let tr = state.tr.setNodeAttribute(pos, 'meta', { ...meta, smartParams });
  if (opts.history === false) tr = tr.setMeta('addToHistory', false);
  if (dispatch) dispatch(tr);
  return true;
}

/**
 * Pick an image. The desktop build answers with the native dialog, the web
 * build's shim answers with the browser picker — one call, both platforms.
 * Returns a data: URL ready for the specImage node, or null on cancel.
 */
export async function pickImageFile(): Promise<{ dataUrl: string; name: string } | null> {
  if (!window.api?.openImageDialog) return null;
  const result = await window.api.openImageDialog();
  if (!result) return null;
  const dataUrl = result.data.startsWith('data:')
    ? result.data
    : `data:${result.mimeType || 'image/png'};base64,${result.data}`;
  return { dataUrl, name: result.name || '' };
}

/** Toggle "skip at export" on the blocks the selection touches. */
export function toggleExcluded(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined
): boolean {
  const targets = selectedBlocks(state).map(({ node, pos }) => ({
    pos,
    excluded: !!node.attrs.excluded,
  }));
  if (!targets.length) return false;
  // One toggle for the whole selection: if any block is included, exclude all.
  const next = targets.some((t) => !t.excluded);
  let tr: Transaction | null = null;
  for (const t of targets) {
    if (t.excluded === next) continue;
    tr = (tr || state.tr).setNodeAttribute(t.pos, 'excluded', next);
  }
  if (!tr) return false;
  if (dispatch) dispatch(tr);
  return true;
}
